import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { ordersApi, type OrderStatus, type OrdersFilters } from '@/services/ordersApi'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'

export function useOrdersList(filters: OrdersFilters, page: number) {
  const tenantId = useAuthStore((state) => state.tenantId)
  const queryFilters = { ...filters, page }

  return useQuery({
    queryKey: queryKeys.ordersList(tenantId, queryFilters),
    queryFn: () => ordersApi.getOrders(queryFilters),
    staleTime: 20_000,
    placeholderData: keepPreviousData,
  })
}

export function useOrdersSummary() {
  const tenantId = useAuthStore((state) => state.tenantId)

  return useQuery({
    queryKey: queryKeys.ordersSummary(tenantId),
    queryFn: () => ordersApi.getSummary(),
    staleTime: 30_000,
  })
}

export function useOrderDetail(id: string | undefined) {
  const tenantId = useAuthStore((state) => state.tenantId)

  return useQuery({
    queryKey: queryKeys.order(tenantId, id ?? 'none'),
    queryFn: () => ordersApi.getOrder(id as string),
    enabled: Boolean(id && id !== 'new'),
  })
}

export interface UpdateOrderStatusInput {
  orderId: string
  status: OrderStatus
  note?: string | null
}

export function useUpdateOrderStatus(orderId: string | undefined) {
  const tenantId = useAuthStore((state) => state.tenantId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderId: targetId, status, note }: UpdateOrderStatusInput) =>
      ordersApi.updateOrderStatus(targetId, { status, note }),
    onSuccess: async () => {
      // Solo las claves de pedidos del tenant actual.
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.ordersSummary(tenantId) })
      if (orderId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.order(tenantId, orderId) })
      }
      toast.success('Estado actualizado')
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.error || error?.message || 'No se pudo actualizar el estado'),
  })
}
